/**
 * A site's nodes (D9, D16, D21, D30): role, link state and last contact, the
 * witness in force and its expected failover, the package versions, the
 * certificate's expiry and the last health report. Actions: make the backup
 * main (swap roles), replace a node (revoke its key and get a code for its
 * successor). Revoked nodes are listed last, without actions.
 */
import type { SiteNode } from '@acp/api-contract/sites.js';
import { RefreshCwIcon } from 'lucide-react';
import { agoText, certState, healthText, shortHash, sortNodes, witnessText } from '../../lib/admin/view.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type NodesTableProps = {
  readonly nodes: readonly SiteNode[];
  readonly nowMs: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly pending: boolean;
  readonly onRetry: () => void;
  readonly onMakeMain: (nodeId: string) => void;
  readonly onReplace: (nodeId: string) => void;
};

const LINK_BADGE = { connected: 'success', disconnected: 'offline', 'never-connected': 'outline' } as const;
const CERT_BADGE = { ok: 'outline', warning: 'warning', expired: 'destructive', none: 'outline' } as const;

type RowProps = { readonly node: SiteNode; readonly nowMs: number; readonly pending: boolean; readonly onMakeMain: (nodeId: string) => void; readonly onReplace: (nodeId: string) => void };

function NodeIdentity({ node }: { readonly node: SiteNode }) {
  return (
    <>
      <div className="font-medium">{node.label}</div>
      <div className="font-mono text-xs text-muted-foreground">
        {node.lanIp ?? '–'} · {node.arch}
      </div>
      {node.dnsName === null ? null : <div className="font-mono text-xs text-muted-foreground">{node.dnsName}</div>}
    </>
  );
}

function NodeActions({ node, pending, onMakeMain, onReplace }: Omit<RowProps, 'nowMs'>) {
  if (node.status === 'revoked') return null;
  return (
    <div className="flex justify-end gap-1">
      {node.role === 'backup' ? (
        <Button size="sm" variant="outline" disabled={pending} onClick={() => onMakeMain(node.nodeId)} data-testid="make-main">
          Make main
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" className="text-destructive" disabled={pending} onClick={() => onReplace(node.nodeId)} data-testid="replace-node">
        Replace
      </Button>
    </div>
  );
}

function NodeRow({ node, nowMs, pending, onMakeMain, onReplace }: RowProps) {
  const cert = certState(node.certExpiresAt, nowMs);
  const revoked = node.status === 'revoked';
  const health = healthText(node.health);
  return (
    <TableRow data-testid="node-row" data-role={node.role} data-status={node.status} className={revoked ? 'opacity-60' : undefined}>
      <TableCell>
        <NodeIdentity node={node} />
      </TableCell>
      <TableCell>{revoked ? <Badge variant="offline">revoked</Badge> : <Badge variant={node.role === 'main' ? 'default' : 'secondary'}>{node.role}</Badge>}</TableCell>
      <TableCell>
        <Badge variant={LINK_BADGE[node.linkState]}>{node.linkState}</Badge>
        <div className="text-xs text-muted-foreground">seen {agoText(node.lastSeen, nowMs)}</div>
      </TableCell>
      <TableCell className="text-sm">{witnessText(node.health) ?? '–'}</TableCell>
      <TableCell className="font-mono text-xs">{node.packageHashes.length === 0 ? '–' : node.packageHashes.map(shortHash).join(' ')}</TableCell>
      <TableCell>
        <Badge variant={CERT_BADGE[cert.tone]} data-testid="cert-state" data-tone={cert.tone}>
          {cert.text}
        </Badge>
      </TableCell>
      <TableCell className="text-xs">{health ?? <span className="text-muted-foreground">no report yet</span>}</TableCell>
      <TableCell>
        <NodeActions node={node} pending={pending} onMakeMain={onMakeMain} onReplace={onReplace} />
      </TableCell>
    </TableRow>
  );
}

export function NodesTable({ nodes, nowMs, loading, error, pending, onRetry, onMakeMain, onReplace }: NodesTableProps) {
  const sorted = sortNodes(nodes);
  return (
    <div className="grid gap-3" data-testid="nodes-table">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load nodes: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {loading && nodes.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Loading nodes…</p> : null}
      {!loading && error === null && nodes.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="nodes-empty">
          No servers yet. “Add server” shows the command that installs one.
        </p>
      ) : null}
      {sorted.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Node</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Link</TableHead>
              <TableHead>Witness</TableHead>
              <TableHead>Versions</TableHead>
              <TableHead>Certificate</TableHead>
              <TableHead>Health</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((n) => (
              <NodeRow key={n.nodeId} node={n} nowMs={nowMs} pending={pending} onMakeMain={onMakeMain} onReplace={onReplace} />
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}
